import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const matmulComponent: ComponentDef = {
  id: 'matmul',
  name: 'Matrix Multiply',
  description: 'Generic batched matrix multiplication A @ B.',
  category: 'function',
  params: [
    { name: 'm', label: 'M (rows of A)', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'k', label: 'K (inner dim)', type: 'number', default: 256, min: 1, max: 8192, step: 1 },
    { name: 'n', label: 'N (cols of B)', type: 'number', default: 512, min: 1, max: 8192, step: 1 },
    { name: 'variant', label: 'Variant', type: 'select', default: 'naive', options: [
      { label: 'Naive', value: 'naive' },
      { label: 'Blocked / Tiled', value: 'blocked' }
    ]}
  ],
  buildGraph: (params) => {
    const m = Number(params.m);
    const k = Number(params.k);
    const n = Number(params.n);
    const variant = String(params.variant || 'naive');
    const g = new GraphBuilder();

    if (variant === 'blocked') {
      // ── BLOCKED / TILED MATMUL ──
      // Choose a tile size for visualization (cap at reasonable values)
      const tileSize = Math.max(1, Math.min(64, Math.min(m, k, n)));
      const numTilesM = Math.ceil(m / tileSize);
      const numTilesK = Math.ceil(k / tileSize);
      const numTilesN = Math.ceil(n / tileSize);

      g.input('A', [m, k]);
      g.input('B', [k, n]);

      // Split A into row-tiles: A -> [A_row0, A_row1, ...]
      // Show up to 3 row-tile groups for clarity
      const showTilesM = Math.min(numTilesM, 3);
      const showTilesN = Math.min(numTilesN, 3);
      const showTilesK = Math.min(numTilesK, 3);

      // Split A along rows into tile strips
      for (let i = 0; i < showTilesM; i++) {
        const rows = Math.min(tileSize, m - i * tileSize);
        g.split(`A_tile_${i}`, 'A', [rows, k], `A row-tile ${i} [${rows}×${k}]`);
      }
      if (numTilesM > showTilesM) {
        g.split('A_tile_more', 'A', [tileSize, k], `… +${numTilesM - showTilesM} more row-tiles`);
      }

      // Split B along columns into tile strips
      for (let j = 0; j < showTilesN; j++) {
        const cols = Math.min(tileSize, n - j * tileSize);
        g.split(`B_tile_${j}`, 'B', [k, cols], `B col-tile ${j} [${k}×${cols}]`);
      }
      if (numTilesN > showTilesN) {
        g.split('B_tile_more', 'B', [k, tileSize], `… +${numTilesN - showTilesN} more col-tiles`);
      }

      // For the first output tile (0,0): accumulate partial products over K tiles
      // Show the inner K-loop for tile (0,0) explicitly
      for (let kk = 0; kk < showTilesK; kk++) {
        const innerDim = Math.min(tileSize, k - kk * tileSize);
        // Split the A tile further along K dimension
        g.split(`A00_k${kk}`, 'A_tile_0', [tileSize, innerDim], `A tile [0,0] k-slice ${kk}`);
        // Split the B tile further along K dimension
        g.split(`B00_k${kk}`, 'B_tile_0', [innerDim, tileSize], `B tile [0,0] k-slice ${kk}`);
        // Partial product for this K slice
        g.matmul(`partial_00_k${kk}`, `A00_k${kk}`, `B00_k${kk}`, [tileSize, tileSize]);
      }

      // Accumulate partial products
      if (showTilesK >= 2) {
        g.add(`accum_00_01`, 'partial_00_k0', 'partial_00_k1');
        if (showTilesK >= 3) {
          g.add(`accum_00_012`, 'accum_00_01', 'partial_00_k2');
          if (numTilesK > showTilesK) {
            g.scale('accum_00_final', 'accum_00_012', `+${numTilesK - showTilesK} more partials`);
          } else {
            g.scale('accum_00_final', 'accum_00_012', 'C[0,0] accumulated');
          }
        } else if (numTilesK > showTilesK) {
          g.scale('accum_00_final', 'accum_00_01', `+${numTilesK - showTilesK} more partials`);
        } else {
          g.scale('accum_00_final', 'accum_00_01', 'C[0,0] accumulated');
        }
      } else {
        g.scale('accum_00_final', 'partial_00_k0', 'C[0,0] = partial product');
      }

      // Show one more output tile to illustrate the grid
      if (showTilesN >= 2) {
        // Compute tile (0,1) with first K slice only for brevity
        g.split(`A01_k0`, 'A_tile_0', [tileSize, tileSize], `A tile [0,1] k-slice 0`);
        g.split(`B01_k0`, 'B_tile_1', [tileSize, tileSize], `B tile [0,1] k-slice 0`);
        g.matmul('partial_01_k0', 'A01_k0', 'B01_k0', [tileSize, tileSize]);
        g.scale('accum_01_final', 'partial_01_k0', `C[0,1] (${showTilesK}+ partials)`);
      }

      // Concatenate output tiles into final C
      const concatIds = ['accum_00_final'];
      if (showTilesN >= 2) concatIds.push('accum_01_final');
      if (numTilesN > showTilesN) concatIds.push('accum_00_final'); // reuse as placeholder
      g.concat('C_assembled', concatIds, [tileSize, n]);
      g.scale('out_note', 'C_assembled', `${numTilesM}×${numTilesN} tile grid → full C [${m}×${n}]`);
      g.output('out', 'out_note');
    } else {
      // ── NAIVE MATMUL ──
      g.input('A', [m, k]);
      g.input('B', [k, n]);
      g.matmul('C', 'A', 'B', [m, n]);
      g.output('out', 'C');
    }

    return g.build();
  }
};
